import { useState } from 'react';
import { Flex } from 'foundations-components/components/Flex';
import { View } from 'foundations-components/transitional/components/View';
import { Button } from 'foundations-components/transitional/components/Button';
import { HR } from 'foundations-components/transitional/components/HR';
import { Paragraph } from 'foundations-components/transitional/components/Paragraph';
import { readBoolean, readNumber, readString } from './blockFieldValues';
import { BreezeArtifactView } from './BreezeArtifactView';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
function InlineText({
  value,
  placeholder,
  ariaLabel,
  fontSize,
  fontWeight,
  onChange
}) {
  // While focused, show what's typed rather than `value`: in the page editor
  // the value round-trips through the editor, so keystrokes typed faster than
  // a round trip would be reverted.
  const [draft, setDraft] = useState(null);
  return /*#__PURE__*/_jsx("input", {
    value: draft !== null && draft !== void 0 ? draft : value,
    placeholder: placeholder,
    "aria-label": ariaLabel,
    onFocus: () => setDraft(value),
    onBlur: () => setDraft(null),
    onChange: event => {
      setDraft(event.target.value);
      onChange(event.target.value);
    },
    style: {
      font: 'inherit',
      fontSize,
      fontWeight,
      // Bleed the editable box outward by its own border + padding so the
      // text keeps its normal layout position instead of being indented.
      inlineSize: 'calc(100% + 14px)',
      marginInlineStart: '-7px',
      boxSizing: 'border-box',
      border: '1px dashed #cbd6e2',
      borderRadius: 4,
      background: 'transparent',
      padding: '2px 6px',
      color: 'inherit'
    }
  });
}
function headerFontSize(level) {
  switch (level) {
    case 1:
      return 32;
    case 2:
      return 26;
    case 3:
      return 22;
    default:
      return 18;
  }
}

/**
 * Renders a single layout block from its field values. In the page editor's
 * canvas, text-based blocks (HEADER/TEXT) are editable inline and report
 * changes via onFieldChange. Block types + field names mirror the reference-be
 * BlockDefinitionRegistry.
 */
export function BlockView({
  block,
  editing,
  onFieldChange
}) {
  switch (block.blockType) {
    case 'HEADER':
      {
        const text = readString(block, 'text');
        const subHeader = readString(block, 'subHeader');
        const fontSize = headerFontSize(readNumber(block, 'level'));
        const subFontSize = Math.max(14, Math.round(fontSize * 0.6));
        const showDivider = readBoolean(block, 'divider');
        return /*#__PURE__*/_jsxs(Flex, {
          flexDirection: "column",
          gap: "50",
          children: [editing ? /*#__PURE__*/_jsx(InlineText, {
            value: text,
            placeholder: "Heading",
            ariaLabel: "Header text",
            fontSize: fontSize,
            fontWeight: 700,
            onChange: value => onFieldChange('text', 'STRING', value)
          }) : /*#__PURE__*/_jsx("span", {
            style: {
              fontSize,
              fontWeight: 700
            },
            children: text || 'Heading'
          }), subHeader ? /*#__PURE__*/_jsx("span", {
            style: {
              fontSize: subFontSize,
              color: '#516f90'
            },
            children: subHeader
          }) : null, showDivider ? /*#__PURE__*/_jsx("hr", {
            style: {
              inlineSize: '100%',
              border: 'none',
              borderTop: '2px solid #7c98b6',
              margin: 0
            }
          }) : null]
        });
      }
    case 'TEXT':
      {
        const text = readString(block, 'text');
        if (editing) {
          return /*#__PURE__*/_jsx(InlineText, {
            value: text,
            placeholder: "Text",
            ariaLabel: "Text content",
            onChange: value => onFieldChange('text', 'STRING', value)
          });
        }
        return /*#__PURE__*/_jsx(Paragraph, {
          children: text
        });
      }
    case 'IMAGE':
      {
        const url = readString(block, 'url');
        const alt = readString(block, 'alt');
        const rounded = readBoolean(block, 'rounded');
        if (!url) {
          return /*#__PURE__*/_jsx(View, {
            padding: "500",
            backgroundColor: "fill-surface-raised",
            color: "text-core-subtle",
            children: "No image URL set"
          });
        }
        return /*#__PURE__*/_jsx("img", {
          src: url,
          alt: alt,
          style: {
            maxWidth: '100%',
            borderRadius: rounded ? 12 : 0
          }
        });
      }
    case 'BUTTON':
      {
        const label = readString(block, 'label');
        return /*#__PURE__*/_jsx(Button, {
          use: "primary",
          size: "medium",
          children: label || 'Button'
        });
      }
    case 'DIVIDER':
      return /*#__PURE__*/_jsx(HR, {
        distance: "sm",
        blockSize: "2px",
        backgroundColor: "border-core-default"
      });
    case 'BREEZE_ARTIFACT':
      {
        const accessToken = readString(block, 'accessToken');
        return /*#__PURE__*/_jsx(BreezeArtifactView, {
          accessToken: accessToken,
          editing: editing
        });
      }
    default:
      return /*#__PURE__*/_jsxs(View, {
        color: "text-core-subtle",
        children: ["Unknown block: ", block.blockType]
      });
  }
}